/**
 * OneDriveConnector - Composant de connexion et configuration OneDrive
 * Permet de connecter OneDrive, sélectionner un dossier, synchroniser et importer des fichiers
 * Supporte la synchronisation automatique en temps réel via webhooks Microsoft Graph
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Cloud, Folder, FolderOpen, ChevronRight,
  Check, Loader2, RefreshCw, Unplug, History,
  FileText, AlertCircle, CheckCircle2, Clock, ArrowLeft,
  Download, Upload, XCircle, Zap, ZapOff, Settings, Mail, Plus, X, AlertTriangle
} from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Status badges for history
const STATUS_CONFIG = {
  success: { label: "Succès", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  error: { label: "Erreur", color: "bg-red-100 text-red-700", icon: AlertCircle },
  pending: { label: "En cours", color: "bg-amber-100 text-amber-700", icon: Clock },
};

// Event type labels
const EVENT_LABELS = {
  file_detected: "Fichier détecté",
  file_downloaded: "Téléchargé",
  ocr_processed: "OCR traité",
  invoice_created: "Facture créée",
  moved_to_traite: "Déplacé vers Traité",
  skipped: "Ignoré (doublon)",
  error: "Erreur"
};

export default function OneDriveConnector({ onConnectionChange, onImportSuccess }) {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connection, setConnection] = useState(null);
  
  // Folder selection
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState("root");
  const [currentFolderPath, setCurrentFolderPath] = useState("/");
  const [folderHistory, setFolderHistory] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectingFolder, setSelectingFolder] = useState(false);
  
  // Files and import
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  
  // Auto-sync (webhooks)
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [togglingAutoSync, setTogglingAutoSync] = useState(false);
  const [subscriptionExpiry, setSubscriptionExpiry] = useState(null);
  
  // History
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Settings (email alerts)
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settings, setSettings] = useState({
    alert_email: "",
    cc_emails: [],
    alert_on_error: true
  });
  const [newCcEmail, setNewCcEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Disconnect confirmation
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const token = localStorage.getItem("token");

  // Check connection status
  const checkStatus = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/onedrive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const conn = res.data.connected ? res.data.connection : null;
      setConnection(conn);
      onConnectionChange?.(res.data.connected);
      
      // Check auto-sync status if connected
      if (conn) {
        try {
          const subRes = await axios.get(`${API}/api/onedrive/subscribe/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAutoSyncEnabled(subRes.data.auto_sync_enabled);
          setSubscriptionExpiry(subRes.data.subscription?.expires_at);
        } catch (err) {
          console.error("Error checking subscription status:", err);
        }
      }
    } catch (err) {
      console.error("Error checking OneDrive status:", err);
    } finally {
      setLoading(false);
    }
  }, [token]); // Remove onConnectionChange from dependencies to avoid loops

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Listen for OAuth callback
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'onedrive_connected') {
        toast.success(`OneDrive connecté : ${event.data.email}`);
        checkStatus();
        setConnecting(false);
        // Open folder selection dialog
        setTimeout(() => setShowFolderDialog(true), 500);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkStatus]);

  // Start OAuth flow
  const startConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/api/onedrive/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open popup for Microsoft login
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        res.data.auth_url,
        'onedrive_auth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );
    } catch (err) {
      console.error("Error starting OAuth:", err);
      toast.error("Erreur lors de la connexion");
      setConnecting(false);
    }
  };

  // Disconnect
  const disconnect = async () => {
    try {
      await axios.post(`${API}/api/onedrive/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnection(null);
      setAutoSyncEnabled(false);
      onConnectionChange?.(false);
      toast.success("OneDrive déconnecté");
    } catch (err) {
      console.error("Error disconnecting:", err);
      toast.error("Erreur lors de la déconnexion");
    }
    setShowDisconnectConfirm(false);
  };

  // Load folders
  const loadFolders = async (folderId = "root") => {
    setLoadingFolders(true);
    try {
      const res = await axios.get(`${API}/api/onedrive/folders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { folder_id: folderId }
      });
      setFolders(res.data.folders);
      setCurrentFolderId(res.data.current_folder_id);
      setCurrentFolderPath(res.data.current_folder_path);
    } catch (err) {
      console.error("Error loading folders:", err);
      toast.error("Erreur lors du chargement des dossiers");
    } finally {
      setLoadingFolders(false);
    }
  };

  // Navigate to folder
  const navigateToFolder = (folderId) => {
    setFolderHistory(prev => [...prev, { id: currentFolderId, path: currentFolderPath }]);
    loadFolders(folderId);
  };

  // Navigate back
  const navigateBack = () => {
    if (folderHistory.length > 0) {
      const prev = folderHistory[folderHistory.length - 1];
      setFolderHistory(h => h.slice(0, -1));
      loadFolders(prev.id);
    }
  };

  // Select folder
  const selectFolder = async () => {
    setSelectingFolder(true);
    try {
      const res = await axios.post(
        `${API}/api/onedrive/watch-folder`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { folder_id: currentFolderId }
        }
      );
      
      toast.success(`Dossier "${res.data.folder_path}" configuré`);
      setShowFolderDialog(false);
      checkStatus();
    } catch (err) {
      console.error("Error selecting folder:", err);
      toast.error("Erreur lors de la sélection du dossier");
    } finally {
      setSelectingFolder(false);
    }
  };

  // Toggle auto-sync
  const toggleAutoSync = async (enabled) => {
    setTogglingAutoSync(true);
    try {
      if (enabled) {
        // Enable auto-sync (create subscription)
        const res = await axios.post(`${API}/api/onedrive/subscribe`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoSyncEnabled(true);
        setSubscriptionExpiry(res.data.expires_at);
        toast.success("Sync automatique activée ! Les nouveaux fichiers seront importés automatiquement.", {
          duration: 5000,
          icon: <Zap className="h-5 w-5 text-yellow-500" />
        });
      } else {
        // Disable auto-sync
        await axios.delete(`${API}/api/onedrive/subscribe`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoSyncEnabled(false);
        setSubscriptionExpiry(null);
        toast.info("Sync automatique désactivée");
      }
    } catch (err) {
      console.error("Error toggling auto-sync:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la configuration");
      // Revert UI state
      setAutoSyncEnabled(!enabled);
    } finally {
      setTogglingAutoSync(false);
    }
  };

  // Trigger sync
  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSelectedFiles(new Set());
    setImportResults(null);
    try {
      const res = await axios.post(`${API}/api/onedrive/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncResult(res.data);
      
      if (res.data.files_found > 0) {
        // Auto-select all files
        setSelectedFiles(new Set(res.data.files.map(f => f.id)));
        setShowImportDialog(true);
      } else {
        toast.info("Aucun fichier à importer");
      }
    } catch (err) {
      console.error("Error syncing:", err);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedFiles.size === syncResult?.files?.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(syncResult.files.map(f => f.id)));
    }
  };

  // Import selected files - ONE BY ONE with real-time progress
  const importSelectedFiles = async () => {
    if (selectedFiles.size === 0) {
      toast.error("Sélectionnez au moins un fichier");
      return;
    }
    
    setImporting(true);
    setImportProgress(0);
    
    // Initialize results with all files as "pending"
    const fileIds = Array.from(selectedFiles);
    const initialResults = {
      total: fileIds.length,
      success: 0,
      failed: 0,
      skipped: 0,
      results: fileIds.map(id => {
        const file = syncResult?.files?.find(f => f.id === id);
        return {
          file_id: id,
          filename: file?.name || id,
          status: "pending",
          message: "En attente..."
        };
      })
    };
    setImportResults(initialResults);
    
    let successCount = 0;
    let failedCount = 0;
    let skippedCount = 0;
    
    // Process files one by one
    for (let i = 0; i < fileIds.length; i++) {
      const fileId = fileIds[i];
      const file = syncResult?.files?.find(f => f.id === fileId);
      
      // Update status to "processing"
      setImportResults(prev => ({
        ...prev,
        results: prev.results.map(r => 
          r.file_id === fileId 
            ? { ...r, status: "processing", message: "Traitement en cours..." }
            : r
        )
      }));
      
      try {
        const res = await axios.post(
          `${API}/api/onedrive/import/${fileId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.data.success) {
          if (res.data.skipped) {
            skippedCount++;
            setImportResults(prev => ({
              ...prev,
              skipped: skippedCount,
              results: prev.results.map(r => 
                r.file_id === fileId 
                  ? { 
                      ...r, 
                      status: "skipped", 
                      message: res.data.message || "Doublon - déplacé vers Doublons",
                      invoice_numero: res.data.invoice_numero
                    }
                  : r
              )
            }));
          } else {
            successCount++;
            setImportResults(prev => ({
              ...prev,
              success: successCount,
              results: prev.results.map(r => 
                r.file_id === fileId 
                  ? { 
                      ...r, 
                      status: "success", 
                      message: res.data.message || "Facture importée avec succès",
                      invoice_numero: res.data.invoice_numero,
                      fournisseur: res.data.fournisseur,
                      total_ttc: res.data.total_ttc
                    }
                  : r
              )
            }));
          }
        } else {
          failedCount++;
          setImportResults(prev => ({
            ...prev,
            failed: failedCount,
            results: prev.results.map(r => 
              r.file_id === fileId 
                ? { ...r, status: "error", message: res.data.message || "Erreur d'import" }
                : r
            )
          }));
        }
      } catch (err) {
        failedCount++;
        setImportResults(prev => ({
          ...prev,
          failed: failedCount,
          results: prev.results.map(r => 
            r.file_id === fileId 
              ? { ...r, status: "error", message: err.response?.data?.detail || "Erreur de connexion" }
              : r
          )
        }));
      }
      
      // Update progress
      setImportProgress(Math.round(((i + 1) / fileIds.length) * 100));
    }
    
    // Final toast
    if (successCount > 0) {
      toast.success(`${successCount} facture(s) importée(s) avec succès`);
      onImportSuccess?.();
    }
    if (skippedCount > 0) {
      toast.info(`${skippedCount} doublon(s) déplacé(s) vers Doublons`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} fichier(s) en erreur`);
    }
    
    setImporting(false);
    // Do NOT close the dialog - let user see the results
  };

  // Load history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/api/onedrive/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history);
    } catch (err) {
      console.error("Error loading history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Open folder dialog
  const openFolderSelection = () => {
    setFolderHistory([]);
    loadFolders("root");
    setShowFolderDialog(true);
  };

  // Open history dialog
  const openHistory = () => {
    loadHistory();
    setShowHistoryDialog(true);
  };

  // Load settings
  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/onedrive/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        alert_email: res.data.alert_email || "",
        cc_emails: res.data.cc_emails || [],
        alert_on_error: res.data.alert_on_error !== false
      });
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };

  // Open settings dialog
  const openSettings = () => {
    loadSettings();
    setNewCcEmail("");
    setShowSettingsDialog(true);
  };

  // Add CC email(s) - supports multiple emails separated by comma
  const addCcEmail = () => {
    const input = newCcEmail.trim();
    if (!input) return;
    
    // Split by comma and clean up
    const emails = input.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const validEmails = [];
    const invalidEmails = [];
    const duplicateEmails = [];
    
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        invalidEmails.push(email);
      } else if (settings.cc_emails.includes(email) || validEmails.includes(email)) {
        duplicateEmails.push(email);
      } else {
        validEmails.push(email);
      }
    }
    
    // Show errors if any
    if (invalidEmails.length > 0) {
      toast.error(`Email(s) invalide(s): ${invalidEmails.join(', ')}`);
    }
    if (duplicateEmails.length > 0) {
      toast.warning(`Déjà ajouté(s): ${duplicateEmails.join(', ')}`);
    }
    
    // Add valid emails
    if (validEmails.length > 0) {
      setSettings(prev => ({
        ...prev,
        cc_emails: [...prev.cc_emails, ...validEmails]
      }));
      if (validEmails.length > 1) {
        toast.success(`${validEmails.length} emails ajoutés`);
      }
    }
    
    setNewCcEmail("");
  };

  // Remove CC email
  const removeCcEmail = (email) => {
    setSettings(prev => ({
      ...prev,
      cc_emails: prev.cc_emails.filter(e => e !== email)
    }));
  };

  // Save settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/api/onedrive/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Paramètres enregistrés");
      setShowSettingsDialog(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Cloud className="h-5 w-5 text-blue-600" />
            Connecteur OneDrive
          </CardTitle>
          <CardDescription>
            Importez automatiquement vos factures depuis OneDrive
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connection ? (
            <div className="space-y-4">
              {/* Connection info */}
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-emerald-900">Connecté</p>
                    <p className="text-sm text-emerald-700">{connection.microsoft_email}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Unplug className="h-4 w-4 mr-1" />
                  Déconnecter
                </Button>
              </div>

              {/* Watched folder */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-sm text-slate-500">Dossier surveillé</p>
                      <p className="font-medium">
                        {connection.watched_folder_path || "Non configuré"}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={openFolderSelection}>
                    {connection.watched_folder_path ? "Modifier" : "Sélectionner"}
                  </Button>
                </div>
              </div>

              {/* Auto-sync toggle */}
              {connection.watched_folder_path && (
                <div className={`p-3 rounded-lg border transition-colors ${
                  autoSyncEnabled 
                    ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200' 
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {autoSyncEnabled ? (
                        <div className="w-9 h-9 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Zap className="h-5 w-5 text-yellow-600" />
                        </div>
                      ) : (
                        <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                          <ZapOff className="h-5 w-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm">Sync automatique</p>
                        <p className="text-xs text-slate-500">
                          {autoSyncEnabled 
                            ? "Les fichiers sont importés automatiquement" 
                            : "Import manuel uniquement"
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {togglingAutoSync && (
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      )}
                      <Switch
                        checked={autoSyncEnabled}
                        onCheckedChange={toggleAutoSync}
                        disabled={togglingAutoSync}
                      />
                    </div>
                  </div>
                  {autoSyncEnabled && subscriptionExpiry && (
                    <p className="text-xs text-yellow-700 mt-2 pl-12">
                      Renouvellement auto. jusqu'au {new Date(subscriptionExpiry).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={triggerSync}
                  disabled={!connection.watched_folder_path || syncing}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Scanner les fichiers
                </Button>
                <Button variant="outline" onClick={openHistory}>
                  <History className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={openSettings}>
                  <Settings className="h-4 w-4" />
                </Button>
              </div>

              {/* Last sync info */}
              {connection.last_sync_at && (
                <p className="text-xs text-slate-500 text-center">
                  Dernière synchronisation : {new Date(connection.last_sync_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                </p>
              )}
            </div>
          ) : (
            // Not connected
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cloud className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-slate-600 mb-4">
                Connectez votre OneDrive pour importer automatiquement vos factures
              </p>
              <Button onClick={startConnect} disabled={connecting}>
                {connecting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Cloud className="h-4 w-4 mr-2" />
                )}
                Connecter OneDrive
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Folder Selection Dialog */}
      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-amber-600" />
              Sélectionner le dossier parent
            </DialogTitle>
            <DialogDescription>
              Choisissez le dossier contenant vos factures (les sous-dossiers seront aussi surveillés)
            </DialogDescription>
          </DialogHeader>

          {/* Current path */}
          <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg">
            {folderHistory.length > 0 && (
              <Button variant="ghost" size="sm" onClick={navigateBack} disabled={loadingFolders}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <span className="text-sm font-mono flex-1 truncate">{currentFolderPath}</span>
          </div>

          {/* Folders list */}
          <ScrollArea className="h-[300px] border rounded-lg">
            {loadingFolders ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : folders.length > 0 ? (
              <div className="p-1">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => navigateToFolder(folder.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-100 rounded-lg text-left transition-colors"
                  >
                    <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="flex-1 truncate">{folder.name}</span>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Folder className="h-12 w-12 mb-2 opacity-50" />
                <p>Aucun sous-dossier</p>
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderDialog(false)}>
              Annuler
            </Button>
            <Button onClick={selectFolder} disabled={selectingFolder}>
              {selectingFolder && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Check className="h-4 w-4 mr-2" />
              Sélectionner ce dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Importer les fichiers
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les fichiers à importer depuis OneDrive. Les fichiers seront traités par OCR et déplacés vers le dossier "Traité".
            </DialogDescription>
          </DialogHeader>

          {importResults ? (
            // Import results view with real-time timeline
            <div className="space-y-4">
              {/* Progress header */}
              <div className="flex gap-3">
                <div className="flex-1 p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                  <div className="text-2xl font-bold text-emerald-600">{importResults.success || 0}</div>
                  <div className="text-xs text-emerald-700">Réussis</div>
                </div>
                <div className="flex-1 p-3 bg-amber-50 rounded-lg border border-amber-200 text-center">
                  <div className="text-2xl font-bold text-amber-600">{importResults.skipped || 0}</div>
                  <div className="text-xs text-amber-700">Doublons</div>
                </div>
                <div className="flex-1 p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <div className="text-2xl font-bold text-red-600">{importResults.failed || 0}</div>
                  <div className="text-xs text-red-700">Échoués</div>
                </div>
              </div>

              {/* Progress bar */}
              {importing && (
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Progression</span>
                    <span>{importProgress}%</span>
                  </div>
                  <Progress value={importProgress} className="h-2" />
                </div>
              )}

              {/* Timeline of all files */}
              <ScrollArea className="h-[350px] border rounded-lg">
                <div className="p-2 space-y-2">
                  {importResults.results?.map((result, i) => (
                    <div
                      key={result.file_id || i}
                      className={`p-3 rounded-lg border transition-all ${
                        result.status === 'success' 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : result.status === 'skipped'
                          ? 'bg-amber-50 border-amber-200'
                          : result.status === 'error'
                          ? 'bg-red-50 border-red-200'
                          : result.status === 'processing'
                          ? 'bg-blue-50 border-blue-300 animate-pulse'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Status icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {result.status === 'success' && (
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                          )}
                          {result.status === 'skipped' && (
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                          )}
                          {result.status === 'error' && (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                          {result.status === 'processing' && (
                            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                          )}
                          {result.status === 'pending' && (
                            <Clock className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        
                        {/* File info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm">{result.filename}</p>
                          <p className={`text-xs ${
                            result.status === 'success' ? 'text-emerald-700' :
                            result.status === 'skipped' ? 'text-amber-700' :
                            result.status === 'error' ? 'text-red-700' :
                            result.status === 'processing' ? 'text-blue-700' :
                            'text-slate-500'
                          }`}>
                            {result.message}
                          </p>
                          {result.status === 'success' && result.fournisseur && (
                            <p className="text-xs text-emerald-600 mt-1">
                              {result.fournisseur} • {result.total_ttc}€ • N°{result.invoice_numero}
                            </p>
                          )}
                          {result.status === 'skipped' && result.invoice_numero && (
                            <p className="text-xs text-amber-600 mt-1">
                              Facture existante N°{result.invoice_numero}
                            </p>
                          )}
                        </div>
                        
                        {/* Status badge */}
                        <Badge 
                          variant="outline" 
                          className={`flex-shrink-0 ${
                            result.status === 'success' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                            result.status === 'skipped' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            result.status === 'error' ? 'bg-red-100 text-red-700 border-red-300' :
                            result.status === 'processing' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                            'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {result.status === 'success' && 'Importé'}
                          {result.status === 'skipped' && 'Doublon'}
                          {result.status === 'error' && 'Erreur'}
                          {result.status === 'processing' && 'En cours...'}
                          {result.status === 'pending' && 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <DialogFooter>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowImportDialog(false);
                    setSyncResult(null);
                    setImportResults(null);
                  }}
                  disabled={importing}
                >
                  {importing ? "Import en cours..." : "Fermer"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // File selection view
            <>
              {/* Select all header */}
              <div className="flex items-center justify-between p-2 bg-slate-100 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={selectedFiles.size === syncResult?.files?.length && syncResult?.files?.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-sm font-medium">
                    {selectedFiles.size === syncResult?.files?.length 
                      ? "Tout désélectionner" 
                      : "Tout sélectionner"
                    }
                  </span>
                </label>
                <Badge variant="outline">
                  {selectedFiles.size} / {syncResult?.files?.length || 0} sélectionné(s)
                </Badge>
              </div>

              {/* Files list */}
              <ScrollArea className="h-[300px] border rounded-lg">
                <div className="p-2 space-y-1">
                  {syncResult?.files?.map((file) => (
                    <label
                      key={file.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedFiles.has(file.id) 
                          ? 'bg-blue-50 border border-blue-200' 
                          : 'hover:bg-slate-50 border border-transparent'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedFiles.has(file.id)}
                        onCheckedChange={() => toggleFileSelection(file.id)}
                      />
                      <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{file.name}</p>
                        <p className="text-xs text-slate-500">
                          {file.parent_folder && <span className="text-blue-600">{file.parent_folder}</span>}
                          {file.parent_folder && " • "}
                          {(file.size / 1024).toFixed(1)} Ko
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </ScrollArea>

              {importing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Import en cours...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <Progress value={importProgress} />
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowImportDialog(false)} disabled={importing}>
                  Annuler
                </Button>
                <Button 
                  onClick={importSelectedFiles} 
                  disabled={selectedFiles.size === 0 || importing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Importer {selectedFiles.size > 0 ? `(${selectedFiles.size})` : ''}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historique des imports
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="h-[400px]">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-2">
                {history.map((event) => {
                  const StatusIcon = STATUS_CONFIG[event.status]?.icon || Clock;
                  return (
                    <div
                      key={event.id}
                      className="flex items-start gap-3 p-3 border rounded-lg"
                    >
                      <div className={`p-1.5 rounded-full ${STATUS_CONFIG[event.status]?.color || 'bg-slate-100'}`}>
                        <StatusIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{event.file_name}</p>
                          <Badge variant="outline" className="text-xs">
                            {EVENT_LABELS[event.event_type] || event.event_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{event.details}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(event.created_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                        </p>
                      </div>
                      <Badge className={STATUS_CONFIG[event.status]?.color}>
                        {STATUS_CONFIG[event.status]?.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <History className="h-12 w-12 mb-2 opacity-50" />
                <p>Aucun historique</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation */}
      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Déconnecter OneDrive ?</AlertDialogTitle>
            <AlertDialogDescription>
              La synchronisation automatique des factures sera arrêtée.
              Vous pourrez vous reconnecter à tout moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} className="bg-red-600 hover:bg-red-700">
              Déconnecter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Paramètres des alertes
            </DialogTitle>
            <DialogDescription>
              Configurez les emails de notification en cas d'erreur d'import
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Main alert email */}
            <div className="space-y-2">
              <Label htmlFor="alert_email">Email principal</Label>
              <Input
                id="alert_email"
                type="email"
                placeholder="comptabilite@betaye.fr"
                value={settings.alert_email}
                onChange={(e) => setSettings(prev => ({ ...prev, alert_email: e.target.value }))}
              />
              <p className="text-xs text-slate-500">
                Destinataire principal des alertes d'erreur
              </p>
            </div>

            {/* CC emails */}
            <div className="space-y-2">
              <Label>Emails en copie (CC)</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="autre@betaye.fr"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCcEmail())}
                />
                <Button type="button" variant="outline" onClick={addCcEmail}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {/* CC list */}
              {settings.cc_emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {settings.cc_emails.map((email) => (
                    <Badge 
                      key={email} 
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {email}
                      <button
                        onClick={() => removeCcEmail(email)}
                        className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Alert toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Alertes activées</p>
                <p className="text-xs text-slate-500">Recevoir un email en cas d'erreur d'import</p>
              </div>
              <Switch
                checked={settings.alert_on_error}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, alert_on_error: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Annuler
            </Button>
            <Button onClick={saveSettings} disabled={savingSettings}>
              {savingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
